import React, { useState } from "react";
import { X } from "lucide-react";
import { Field, inputCls } from "@/components/common";
import { ImageField } from "@/admin/ImageField";

const KINDS = [["virtual", "Video Consultation photo"], ["concierge", "Private Concierge photo"], ["phone", "Phone Consultation photo"]];
const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const DEF = (d: string) => ({ open: d !== "sun", start: "10:00", end: d === "sat" ? "15:00" : "17:00" });
const mini = "border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-neutral-900";

function HoursTable({ hours, onChange }: any) {
  return (
    <div className="space-y-2" data-testid="consult-hours">
      {DAYS.map(([k, l]) => {
        const h = { ...DEF(k), ...(hours?.[k] || {}) };
        const up = (p: any) => onChange({ ...(hours || {}), [k]: { ...h, ...p } });
        return (
          <div key={k} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`consult-hours-${k}`}>
            <label className="w-32 flex items-center gap-2"><input type="checkbox" checked={h.open} onChange={(e) => up({ open: e.target.checked })} data-testid={`consult-hours-open-${k}`} />{l}</label>
            {h.open ? (<>
              <input type="time" step={900} value={h.start} onChange={(e) => up({ start: e.target.value })} className={mini} data-testid={`consult-hours-start-${k}`} />
              <span className="text-neutral-500">to</span>
              <input type="time" step={900} value={h.end} onChange={(e) => up({ end: e.target.value })} className={mini} data-testid={`consult-hours-end-${k}`} />
            </>) : <span className="text-neutral-500 text-xs">Closed</span>}
          </div>
        );
      })}
    </div>
  );
}

function BlockedList({ items, onChange, label, type, testId }: any) {
  const [v, setV] = useState("");
  const [note, setNote] = useState("");
  const list: any[] = items || [];
  const add = () => { if (!v) return; onChange([...list, type === "date" ? { date: v, note } : v.replace("T", " ")]); setV(""); setNote(""); };
  return (
    <div data-testid={testId}>
      <p className="text-xs text-neutral-600 mb-2">{label}</p>
      <div className="flex flex-nowrap gap-2 mb-3">
        <input type={type === "date" ? "date" : "datetime-local"} step={900} value={v} onChange={(e) => setV(e.target.value)} className={`${mini} min-w-0`} data-testid={`${testId}-input`} />
        {type === "date" && <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" className={`${mini} min-w-0 flex-1`} data-testid={`${testId}-note`} />}
        <button type="button" onClick={add} className="border border-neutral-900 px-3 text-xs uppercase tracking-wider flex-none" data-testid={`${testId}-add`}>Block</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((b, i) => (
          <span key={i} className="inline-flex items-center gap-2 bg-neutral-100 px-3 py-1 text-xs" data-testid={`${testId}-item-${i}`}>
            {typeof b === "string" ? b : `${b.date}${b.note ? ` · ${b.note}` : ""}`}
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label="Remove"><X size={12} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export function ConsultationEditor({ cfg, onChange }: { cfg: any; onChange: (v: any) => void }) {
  const v = cfg || {};
  const set = (k: string, val: any) => onChange({ ...v, [k]: val });
  const setImg = (k: string, url: string) => set("images", { ...(v.images || {}), [k]: url });
  return (
    <section className="bg-white border border-neutral-200 p-6 space-y-6" data-testid="consultation-editor">
      <p className="eyebrow text-[10px] text-neutral-500">Book a Consultation</p>
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <p className="text-[10px] uppercase tracking-wider text-neutral-500">Weekly hours</p>
          <HoursTable hours={v.hours} onChange={(h: any) => set("hours", h)} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Appointment length"><select value={v.slot_minutes || 60} onChange={(e) => set("slot_minutes", Number(e.target.value))} className={inputCls} data-testid="consult-slot-minutes">{[30, 45, 60, 90, 120].map((n) => <option key={n} value={n}>{n} minutes</option>)}</select></Field>
            <Field label="Minimum notice (hours)"><input type="number" min={0} value={v.lead_hours ?? 24} onChange={(e) => set("lead_hours", e.target.value)} className={inputCls} data-testid="consult-lead-hours" /></Field>
          </div>
        </div>
        <div className="space-y-6">
          <BlockedList type="date" items={v.blocked_dates} onChange={(x: any) => set("blocked_dates", x)} label="Blocked dates (holidays, days off)" testId="consult-blocked-dates" />
          <BlockedList type="slot" items={v.blocked_slots} onChange={(x: any) => set("blocked_slots", x)} label="Blocked single times" testId="consult-blocked-slots" />
          <Field label="Send booking notifications & calendar invites to"><input type="email" value={v.notify_email || ""} onChange={(e) => set("notify_email", e.target.value)} placeholder="you@somnio.co" className={inputCls} data-testid="consult-notify-email" /></Field>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        <Field label="Concierge travel areas — one suburb or city per line">
          <textarea rows={5} value={(v.concierge_areas || []).join("\n")} onChange={(e) => set("concierge_areas", e.target.value.split("\n").map((x) => x.trimStart()))}
            onBlur={() => set("concierge_areas", (v.concierge_areas || []).map((x: string) => x.trim()).filter(Boolean))} className={inputCls} data-testid="concierge-areas-input" />
        </Field>
        <div className="space-y-2">
          <Field label="Travel fee outside these areas (A$, 0 = none)"><input type="number" min={0} value={v.travel_fee ?? 0} onChange={(e) => set("travel_fee", e.target.value)} className={inputCls} data-testid="consult-travel-fee" /></Field>
          <p className="text-xs text-neutral-600">Shown to customers before they book; paid at the visit. Bookings outside your areas are flagged in your Inbox. Leave the areas empty to skip the area check.</p>
          <Field label="Concierge note shown to customers"><input value={v.concierge_note || ""} onChange={(e) => set("concierge_note", e.target.value)} placeholder="Free across Melbourne metro, with no obligation." className={inputCls} data-testid="consult-concierge-note" /></Field>
        </div>
      </div>
      <div className="grid gap-4">
        {KINDS.map(([k, l]) => <Field key={k} label={l}><ImageField value={v.images?.[k]} onChange={(u) => setImg(k, u)} testId={`consult-image-input-${k}`} /></Field>)}
      </div>
    </section>
  );
}
